import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { 
  Users, 
  Search, 
  Phone, 
  Mail, 
  Building, 
  Calendar,
  MessageSquare,
  Loader2,
  Eye,
  Trash2
} from 'lucide-react';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../ui/dialog';
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from '../../ui/pagination';
import { adminApiFetch } from '../../../utils/adminApi';

const AdminGogiCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 10;

  const apiBase = process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : '';

  const fetchCustomers = async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminApiFetch(`${apiBase}/backend/admin_api.php?action=getGogiaiCustomers&page=${page}&limit=${limit}`);
      const result = await response.json();
      if (result.status === 'success') {
        setCustomers(result.data);
        setTotalPages(result.pagination.pages);
        setCurrentPage(result.pagination.page);
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('Müşterileri yüklerken bir hata oluştu: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredCustomers = customers.filter(customer => 
    customer.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.phone.includes(searchTerm) ||
    customer.sector.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const fetchConversations = async (sessionId) => {
    setConversationsLoading(true);
    try {
      // api.php endpoint'i için normal fetch kullan (credentials'a gerek yok, CORS uyumluluğu için)
      const response = await fetch(`${apiBase}/backend/api.php?action=gogiai_conversation_history`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, limit: 50 })
      });
      const result = await response.json();
      if (result.conversations) {
        setConversations(result.conversations);
      } else {
        setConversations([]);
      }
    } catch (err) {
      console.error('Konuşmaları yüklerken hata:', err);
      setConversations([]);
    } finally {
      setConversationsLoading(false);
    }
  };

  const openCustomerDetail = (customer) => {
    setSelectedCustomer(customer);
    setIsDetailModalOpen(true);
    if (customer.session_id) {
      fetchConversations(customer.session_id);
    }
  };

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
      fetchCustomers(page);
    }
  };

  useEffect(() => {
    fetchCustomers(currentPage);
  }, [currentPage]);

  const getGenderIcon = (gender) => {
    return gender === 'male' ? '👨' : gender === 'female' ? '👩' : '👤';
  };

  const getGenderText = (gender) => {
    return gender === 'male' ? 'Erkek' : gender === 'female' ? 'Kadın' : 'Belirtilmemiş';
  };

  return (
    <div className="space-y-4 sm:space-y-6 p-2 sm:p-4">
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="flex items-center text-lg sm:text-xl">
            <Users className="w-5 h-5 mr-2" /> GogiAI Müşterileri
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6">
          {/* Search Bar */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Müşteri ara (isim, telefon, sektör)..."
                value={searchTerm}
                onChange={handleSearch}
                className="pl-10 text-sm sm:text-base"
              />
            </div>
          </div>

          {loading && (
            <div className="flex items-center justify-center p-4">
              <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" /> 
              <span className="text-xs sm:text-sm">Müşteriler yükleniyor...</span>
            </div>
          )}

          {error && (
            <div className="text-red-500 p-4 text-xs sm:text-sm">{error}</div>
          )}

          {!loading && !error && (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Müşteri</TableHead>
                      <TableHead>İletişim</TableHead>
                      <TableHead>Sektör</TableHead>
                      <TableHead>Konuşma Sayısı</TableHead>
                      <TableHead>Kayıt Tarihi</TableHead>
                      <TableHead className="text-right">İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCustomers.map((customer) => (
                      <TableRow key={customer.id}>
                        <TableCell>
                          <div className="flex items-center space-x-3">
                            <div className="text-2xl">{getGenderIcon(customer.gender)}</div>
                            <div>
                              <div className="font-medium">{customer.full_name}</div>
                              <div className="text-sm text-gray-500">{getGenderText(customer.gender)}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            <Phone className="w-4 h-4 text-gray-400" />
                            <span className="text-sm">{customer.phone}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {customer.sector}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-1">
                            <MessageSquare className="w-4 h-4 text-gray-400" />
                            <span>{customer.conversation_count || 0}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            <span className="text-sm">
                              {new Date(customer.created_at).toLocaleDateString('tr-TR')}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openCustomerDetail(customer)}
                          >
                            <Eye className="w-4 h-4 mr-1" /> Detay
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden space-y-3">
                {filteredCustomers.map((customer) => (
                  <Card key={customer.id} className="overflow-hidden">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="text-2xl flex-shrink-0">{getGenderIcon(customer.gender)}</div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm sm:text-base text-gray-900 truncate">{customer.full_name}</div>
                          <div className="text-xs text-gray-500">{getGenderText(customer.gender)}</div>
                        </div>
                      </div>
                      
                      <div className="space-y-2 mb-3">
                        <div className="flex items-center gap-2 text-xs sm:text-sm">
                          <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span className="break-all">{customer.phone}</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs sm:text-sm">
                          <Building className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <Badge variant="outline" className="capitalize text-xs">
                            {customer.sector}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2 text-xs sm:text-sm">
                          <MessageSquare className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span>{customer.conversation_count || 0} konuşma</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs sm:text-sm">
                          <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span>{new Date(customer.created_at).toLocaleDateString('tr-TR')}</span>
                        </div>
                      </div>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openCustomerDetail(customer)}
                        className="w-full text-xs sm:text-sm"
                      >
                        <Eye className="w-4 h-4 mr-1" /> Detay Görüntüle
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Pagination */}
              <Pagination className="mt-4">
                <PaginationContent className="flex-wrap">
                  <PaginationItem>
                    <PaginationPrevious 
                      onClick={() => handlePageChange(currentPage - 1)} 
                      disabled={currentPage === 1}
                      className="text-xs sm:text-sm"
                    />
                  </PaginationItem>
                  {[...Array(Math.min(totalPages, 5))].map((_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <PaginationItem key={i}>
                        <PaginationLink 
                          onClick={() => handlePageChange(pageNum)} 
                          isActive={currentPage === pageNum}
                          className="text-xs sm:text-sm"
                        >
                          {pageNum}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  })}
                  <PaginationItem>
                    <PaginationNext 
                      onClick={() => handlePageChange(currentPage + 1)} 
                      disabled={currentPage === totalPages}
                      className="text-xs sm:text-sm"
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </>
          )}
        </CardContent>
      </Card>

      {/* Customer Detail Modal */}
      <Dialog open={isDetailModalOpen} onOpenChange={setIsDetailModalOpen}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader className="p-0 pb-4">
            <DialogTitle className="text-base sm:text-lg">
              Müşteri Detayı: {selectedCustomer?.full_name}
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Müşteri bilgileri ve konuşma geçmişi
            </DialogDescription>
          </DialogHeader>
          
          {selectedCustomer && (
            <div className="space-y-4 sm:space-y-6">
              {/* Customer Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 p-3 sm:p-4 bg-gray-50 rounded-lg">
                <div>
                  <label className="text-xs sm:text-sm font-medium text-gray-500">Ad Soyad</label>
                  <p className="text-sm sm:text-lg font-semibold truncate">{selectedCustomer.full_name}</p>
                </div>
                <div>
                  <label className="text-xs sm:text-sm font-medium text-gray-500">Cinsiyet</label>
                  <p className="text-sm sm:text-lg">{getGenderText(selectedCustomer.gender)} {getGenderIcon(selectedCustomer.gender)}</p>
                </div>
                <div>
                  <label className="text-xs sm:text-sm font-medium text-gray-500">Telefon</label>
                  <p className="text-sm sm:text-lg break-all">{selectedCustomer.phone}</p>
                </div>
                <div>
                  <label className="text-xs sm:text-sm font-medium text-gray-500">Sektör</label>
                  <p className="text-sm sm:text-lg capitalize">{selectedCustomer.sector}</p>
                </div>
                <div>
                  <label className="text-xs sm:text-sm font-medium text-gray-500">Kayıt Tarihi</label>
                  <p className="text-xs sm:text-lg">{new Date(selectedCustomer.created_at).toLocaleString('tr-TR')}</p>
                </div>
                <div>
                  <label className="text-xs sm:text-sm font-medium text-gray-500">Konuşma Sayısı</label>
                  <p className="text-sm sm:text-lg">{selectedCustomer.conversation_count || 0}</p>
                </div>
              </div>

              {/* Conversations */}
              <div>
                <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 flex items-center">
                  <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  Konuşma Geçmişi
                </h3>
                
                {conversationsLoading ? (
                  <div className="flex items-center justify-center p-4">
                    <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
                    <span className="text-xs sm:text-sm">Konuşmalar yükleniyor...</span>
                  </div>
                ) : conversations.length > 0 ? (
                  <div className="space-y-3 sm:space-y-4 max-h-96 overflow-y-auto">
                    {conversations.map((conv, index) => (
                      <div key={conv.id} className="border rounded-lg p-3 sm:p-4">
                        <div className="flex flex-col sm:flex-row justify-between items-start mb-2 gap-2">
                          <span className="text-xs sm:text-sm text-gray-500">
                            {new Date(conv.created_at).toLocaleString('tr-TR')}
                          </span>
                          <Badge variant="outline" className="text-xs">#{index + 1}</Badge>
                        </div>
                        
                        <div className="space-y-2 sm:space-y-3">
                          {/* User Message */}
                          <div className="bg-blue-50 p-2 sm:p-3 rounded-lg">
                            <div className="flex items-start space-x-2">
                              <div className="w-5 h-5 sm:w-6 sm:h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                                M
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs sm:text-sm font-medium text-blue-900 mb-1">Müşteri</p>
                                <p className="text-xs sm:text-sm text-gray-700 whitespace-pre-wrap break-words">{conv.user_message}</p>
                              </div>
                            </div>
                          </div>
                          
                          {/* AI Response */}
                          <div className="bg-gray-50 p-2 sm:p-3 rounded-lg">
                            <div className="flex items-start space-x-2">
                              <div className="w-5 h-5 sm:w-6 sm:h-6 bg-purple-500 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                                G
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs sm:text-sm font-medium text-purple-900 mb-1">GogiAI</p>
                                <p className="text-xs sm:text-sm text-gray-700 whitespace-pre-wrap break-words">{conv.bot_response}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 sm:py-8 text-gray-500">
                    <MessageSquare className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-2 text-gray-300" />
                    <p className="text-xs sm:text-sm">Henüz konuşma geçmişi bulunmuyor.</p>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter className="p-0 pt-4">
            <Button onClick={() => setIsDetailModalOpen(false)} className="w-full sm:w-auto text-sm sm:text-base">
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminGogiCustomers;
